import React, { useEffect, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../utils/authUtils';

const BLUE = '#47ABDB';

// Matches EventDetail.js/CallForAbstracts.js's page chrome, so arriving here
// from the call-for-abstracts funnel doesn't feel like landing on a
// different, plainer product.
const PageWrapper = styled.div`
  padding-left: 2rem;
  padding-right: 2rem;
  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Crumb = styled.nav`
  max-width: 1700px;
  margin: 0 auto;
  padding: 1.6rem 2rem 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);

  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  span.sep { margin: 0 0.4rem; }
  span.current { color: #fff; font-weight: bold; }
`;

const HeroSection = styled.section`
  background-color: #90C4D9;
  padding: 50px 20px 60px;
  margin-top: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 32px 16px 40px;
  }
`;

// Matches Container's width below, so the hero's text edges line up with the
// form card's instead of the hero looking full-width and the form narrow.
// The blue band itself stays full-bleed - only this inner text block is
// constrained.
const HeroInner = styled.div`
  max-width: 56rem;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 2.4rem;
  margin: 0 0 0.6rem;
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const HeroSubtitle = styled.p`
  color: #fff;
  font-size: 1.15rem;
  margin: 0 0 2rem;
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

// A gradient top stripe (matching the site's brand accent, e.g. BufferAccent
// in EventDetail.js) rather than a plain gray-bordered card, so the form
// reads as part of the same product as the hero above it.
const Card = styled.section`
  position: relative;
  text-align: left;
  background:#fff;
  border:1px solid #dbe3ea;
  padding:1.75rem 1.5rem 1.5rem;
  margin-bottom:1.5rem;
  box-shadow:0 8px 24px rgba(15,23,42,.06);

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background-image: linear-gradient(to right,#4e4094,#14cbde);
  }
`;
const CardTitle = styled.h2`font-size:1.15rem;margin:0 0 1.1rem;color:#111D4E;`;
const Form = styled.form`display:flex;flex-direction:column;gap:1.1rem;`;
const Field = styled.label`display:flex;flex-direction:column;gap:.35rem;font-size:.85rem;font-weight:700;color:#334155;`;
const Hint = styled.span`font-weight:400;font-size:.8rem;color:#64748b;`;
const Row = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem;@media(max-width:600px){grid-template-columns:1fr;}`;
// A narrower Field for a short control (e.g. Language) that no longer needs
// to share a two-column Row now that Event has its own prominent block.
const NarrowField = styled(Field)`max-width:14rem;`;
const Input = styled.input`padding:.6rem .7rem;border:1px solid #cbd5e1;font:inherit;&:focus{outline:none;border-color:${BLUE};}`;
const Select = styled.select`padding:.6rem .7rem;border:1px solid #cbd5e1;font:inherit;background:#fff;`;
// Promoted out of the field grid into its own prominent block - which event
// this submission is for is the single most consequential choice on the
// form (it's locked once saved), so it gets top billing rather than sharing
// a row with Language.
const EventContext = styled.div`
  padding: 1rem 1.2rem;
  margin-bottom: 1.1rem;
  background: #eff8fc;
  border-left: 4px solid ${BLUE};
`;
const EventContextLabel = styled.div`
  font-size: .78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #0f4c68;
  margin-bottom: .5rem;
`;
const EventSelect = styled(Select)`
  font-size: 1.05rem;
  font-weight: 700;
  color: #111D4E;
  border-color: ${BLUE};
  max-width: 28rem;
`;
const Textarea = styled.textarea`width:100%;padding:.6rem .7rem;border:1px solid #cbd5e1;font:inherit;min-height:6rem;resize:vertical;&:focus{outline:none;border-color:${BLUE};}`;
// Co-authors are added one at a time into a chip list rather than typed as
// one free-text blob - still serialized to/from a single comma-separated
// string at the API boundary (SubmissionRequest.coAuthors), so no backend
// change is needed.
const AuthorInputRow = styled.div`display:flex;gap:.5rem;`;
const AddAuthorButton = styled.button`
  flex:none;
  width:2.6rem;
  border:1px solid ${BLUE};
  background:#fff;
  color:${BLUE};
  font-size:1.4rem;
  font-weight:700;
  line-height:1;
  cursor:pointer;
  transition:background-color .2s,color .2s;
  &:hover:not(:disabled){ background:${BLUE}; color:#fff; }
  &:disabled{ opacity:.4; cursor:not-allowed; }
`;
const AuthorChips = styled.div`display:flex;flex-wrap:wrap;gap:.5rem;margin-top:.6rem;`;
const AuthorChip = styled.span`
  display:inline-flex;align-items:center;gap:.4rem;
  padding:.35rem .5rem .35rem .75rem;
  background:#eff8fc;border:1px solid ${BLUE};
  font-size:.85rem;font-weight:600;color:#0f4c68;
`;
const RemoveChipButton = styled.button`
  display:flex;align-items:center;justify-content:center;
  width:1.1rem;height:1.1rem;
  border:none;background:none;padding:0;
  font-size:1rem;line-height:1;color:#0f4c68;cursor:pointer;
  &:hover{ color:#dc2626; }
`;
const Actions = styled.div`display:flex;gap:.6rem;flex-wrap:wrap;`;
const Button = styled.button`display:inline-flex;align-items:center;gap:.4rem;border:0;padding:.65rem 1.1rem;background:${p=>p.$secondary?'#64748b':p.$danger?'#dc2626':BLUE};color:#fff;font-weight:700;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:${p=>p.$secondary?'#475569':p.$danger?'#b91c1c':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const Message = styled.div`padding:.75rem;margin-bottom:1.25rem;background:${p=>p.$error?'#fee2e2':'#dcfce7'};color:${p=>p.$error?'#991b1b':'#166534'};`;
// The HEARTS MATCH field is unusual for a scientific-submissions form (most
// conferences don't ask submitters to propose collaboration ideas), so it
// gets a visually distinct treatment - the same brand gradient used under
// headlines elsewhere, but as a solid frame rather than the dashed/segmented
// variant under event titles (see EventTitle in pages/EventDetail.js).
const HeartsMatchFrame = styled.div`
  padding:2px;
  background-image:linear-gradient(to right,#4e4094,#14cbde);
  &:focus-within{box-shadow:0 0 0 3px rgba(78,64,148,.18);}
`;
const HeartsMatchInner = styled.div`background:#fff;padding:2px;`;

const emptyForm = { eventId: '', title: '', coAuthors: [], phoneNumber: '', language: 'EN', backgroundText: '', methodsText: '', resultsText: '', conclusionText: '', heartsMatch: '' };

const splitCoAuthors = value => (value || '').split(',').map(name => name.trim()).filter(Boolean);

const SubmissionsPage = () => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const location = useLocation();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [coAuthorDraft, setCoAuthorDraft] = useState('');

  const load = async () => {
    try {
      const [{ data: evts }, { data: subs }] = await Promise.all([
        api.get('/api/submissions/events'),
        api.get('/api/submissions/mine'),
      ]);
      setEvents(evts || []);
      setMine(subs || []);

      const params = new URLSearchParams(location.search);

      // Deep-linked from the profile's "My History" (AttendeeProfile.js) with
      // ?edit=<engagement id> for a specific submission - load it into the
      // edit form instead of leaving the blank "new submission" one showing,
      // then strip the param so a later save/reload doesn't re-trigger this.
      const editId = params.get('edit');
      // Deep-linked from the "SUBMIT ABSTRACT" button next to Register/Program
      // on the homepage and event-detail page (MainEvent.js, OtherHeartsEvents.js,
      // EventDetail.js) with ?eventId=<id> - preselect that event on the new-
      // submission form instead of leaving "Select an event..." showing.
      const eventId = params.get('eventId');
      if (editId) {
        const match = (subs || []).find((s) => String(s.id) === editId);
        if (match) startEdit(match);
        navigate('/submissions', { replace: true });
      } else if (eventId && (evts || []).some((e) => String(e.id) === eventId)) {
        setForm((f) => ({ ...f, eventId }));
        navigate('/submissions', { replace: true });
      }
    } catch (err) {
      setMessage({ error: true, text: t('submissions.loadError', 'Could not load your submissions.') });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const startEdit = submission => {
    setEditingId(submission.id);
    setForm({
      eventId: String(submission.eventId), title: submission.title || '', coAuthors: splitCoAuthors(submission.coAuthors),
      phoneNumber: submission.phoneNumber || '',
      language: submission.language || 'EN', backgroundText: submission.backgroundText || '', methodsText: submission.methodsText || '',
      resultsText: submission.resultsText || '', conclusionText: submission.conclusionText || '', heartsMatch: submission.heartsMatch || '',
    });
    setCoAuthorDraft('');
    setMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => { setEditingId(null); setForm(emptyForm); setCoAuthorDraft(''); setMessage(null); };

  const addCoAuthor = () => {
    const name = coAuthorDraft.trim();
    if (!name) return;
    setForm(f => ({ ...f, coAuthors: [...f.coAuthors, name] }));
    setCoAuthorDraft('');
  };

  const removeCoAuthor = index => {
    setForm(f => ({ ...f, coAuthors: f.coAuthors.filter((_, i) => i !== index) }));
  };

  const handleCoAuthorKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCoAuthor();
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    // Include a co-author typed into the draft input but never explicitly
    // added (forgot to click + / press Enter) rather than silently dropping it.
    const draftName = coAuthorDraft.trim();
    const coAuthors = draftName ? [...form.coAuthors, draftName] : form.coAuthors;
    const payload = { ...form, coAuthors: coAuthors.join(', '), eventId: form.eventId ? Number(form.eventId) : null };
    try {
      if (editingId) {
        await api.put(`/api/submissions/${editingId}`, payload);
        setMessage({ text: t('submissions.updated', 'Submission updated.') });
      } else {
        await api.post('/api/submissions', payload);
        setMessage({ text: t('submissions.created', 'Submission received - thank you!') });
      }
      setEditingId(null);
      setForm(emptyForm);
      setCoAuthorDraft('');
      await load();
    } catch (err) {
      setMessage({ error: true, text: (typeof err.response?.data === 'string' && err.response.data) || t('submissions.saveError', 'Could not save your submission.') });
    } finally {
      setSaving(false);
    }
  };

  // Returns whether the withdrawal actually went through, so callers editing
  // the just-withdrawn submission know whether to reset the form.
  const withdraw = async id => {
    if (!window.confirm(t('submissions.withdrawConfirm', 'Withdraw this submission? This cannot be undone.'))) return false;
    try {
      await api.delete(`/api/submissions/${id}`);
      await load();
      return true;
    } catch (err) {
      setMessage({ error: true, text: (typeof err.response?.data === 'string' && err.response.data) || t('submissions.withdrawError', 'Could not withdraw this submission.') });
      return false;
    }
  };

  const withdrawCurrent = async () => {
    if (await withdraw(editingId)) {
      setEditingId(null);
      setForm(emptyForm);
    }
  };

  // The "My submissions" browsing list (with its own per-item Edit/Withdraw)
  // was removed - that overview now lives on the profile page's history -
  // but Withdraw needs to stay reachable somewhere while actively editing a
  // submission, gated the same way the old list gated it.
  const editingSubmission = mine.find(s => String(s.id) === String(editingId));
  const editingWithdrawable = editingSubmission
    && (editingSubmission.status === 'SUBMITTED' || editingSubmission.status === 'UNDER_REVIEW');

  return (
    <PageWrapper>
      <Crumb aria-label="Breadcrumb">
        <Link to="/">{t('nav.home', 'Home')}</Link>
        <span className="sep">/</span>
        <Link to="/call-for-abstracts">{t('callForAbstracts.breadcrumb', 'Call for Abstracts')}</Link>
        <span className="sep">/</span>
        <span className="current">{t('submissions.title', 'Scientific Submissions')}</span>
      </Crumb>

      <HeroSection>
        <HeroInner>
          <HeroTitle><FormattedMessage id="submissions.title" defaultMessage="Scientific Submissions" /></HeroTitle>
          <HeroSubtitle><FormattedMessage id="submissions.subtitle" defaultMessage="Submit a scientific contribution for an upcoming HEARTS event." /></HeroSubtitle>

          {message && <Message $error={message.error}>{message.text}</Message>}

          <Card>
          <CardTitle>
            {editingId ? t('submissions.form.editTitle', 'Edit submission') : t('submissions.form.newTitle', 'New submission')}
          </CardTitle>
          <Form onSubmit={handleSubmit}>
            <EventContext>
              <EventContextLabel>{t('submissions.field.event', 'Event')}</EventContextLabel>
              <EventSelect name="eventId" value={form.eventId} onChange={handleChange} required disabled={!!editingId}>
                <option value="">{t('submissions.field.eventPlaceholder', 'Select an event…')}</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </EventSelect>
            </EventContext>
            <NarrowField>
              {t('submissions.field.language', 'Language')}
              <Select name="language" value={form.language} onChange={handleChange}>
                <option value="EN">{t('submissions.field.languageEnglish', 'English')}</option>
                <option value="DE">{t('submissions.field.languageGerman', 'Deutsch')}</option>
              </Select>
            </NarrowField>
            <Field>
              {t('submissions.field.title', 'Title')}
              <Input name="title" value={form.title} onChange={handleChange} required maxLength={300} />
            </Field>
          <Row>
            <Field as="div">
              {t('submissions.field.coAuthors', 'Co-authors')} <Hint>{t('submissions.field.coAuthorsHint', '(optional)')}</Hint>
              <AuthorInputRow>
                <Input
                  value={coAuthorDraft}
                  onChange={e => setCoAuthorDraft(e.target.value)}
                  onKeyDown={handleCoAuthorKeyDown}
                  placeholder={t('submissions.field.coAuthorsPlaceholder', 'Add a co-author…')}
                />
                <AddAuthorButton
                  type="button"
                  onClick={addCoAuthor}
                  disabled={!coAuthorDraft.trim()}
                  aria-label={t('submissions.field.coAuthorsAdd', 'Add co-author')}
                >
                  +
                </AddAuthorButton>
              </AuthorInputRow>
              {form.coAuthors.length > 0 && (
                <AuthorChips>
                  {form.coAuthors.map((name, index) => (
                    <AuthorChip key={`${name}-${index}`}>
                      {name}
                      <RemoveChipButton
                        type="button"
                        onClick={() => removeCoAuthor(index)}
                        aria-label={t('submissions.field.coAuthorsRemove', 'Remove {name}', { name })}
                      >
                        ×
                      </RemoveChipButton>
                    </AuthorChip>
                  ))}
                </AuthorChips>
              )}
            </Field>
            <Field>
              {t('submissions.field.phoneNumber', 'Phone number')} <Hint>{t('submissions.field.phoneNumberHint', '(optional)')}</Hint>
              <Input type="tel" name="phoneNumber" value={form.phoneNumber} onChange={handleChange} />
            </Field>
          </Row>
          <Field>
            {t('submissions.field.background', 'Background')}
            <Textarea name="backgroundText" value={form.backgroundText} onChange={handleChange} />
          </Field>
          <Field>
            {t('submissions.field.methods', 'Methods')}
            <Textarea name="methodsText" value={form.methodsText} onChange={handleChange} />
          </Field>
          <Field>
            {t('submissions.field.results', 'Results')}
            <Textarea name="resultsText" value={form.resultsText} onChange={handleChange} />
          </Field>
          <Field>
            {t('submissions.field.conclusion', 'Conclusion')}
            <Textarea name="conclusionText" value={form.conclusionText} onChange={handleChange} />
          </Field>
          <Field>
            {t('submissions.field.heartsMatch', 'HEARTS MATCH')} <Hint>{t('submissions.field.heartsMatchHint', 'Propose up to four ideas for research collaboration and expansion based on the submitted study.')}</Hint>
            <HeartsMatchFrame>
              <HeartsMatchInner>
                <Textarea name="heartsMatch" value={form.heartsMatch} onChange={handleChange} style={{ border: 'none', padding: '.55rem .6rem' }} />
              </HeartsMatchInner>
            </HeartsMatchFrame>
          </Field>
          <Actions>
            <Button type="submit" disabled={saving}>
              {saving ? t('submissions.saving', 'Saving…') : (editingId ? t('submissions.save', 'Save changes') : t('submissions.submit', 'Submit'))}
            </Button>
            {editingId && (
              <Button type="button" $secondary onClick={cancelEdit} disabled={saving}>
                <FormattedMessage id="submissions.cancel" defaultMessage="Cancel" />
              </Button>
            )}
            {editingWithdrawable && (
              <Button type="button" $danger onClick={withdrawCurrent} disabled={saving}>
                <FormattedMessage id="submissions.mine.withdraw" defaultMessage="Withdraw" />
              </Button>
            )}
          </Actions>
        </Form>
          </Card>
        </HeroInner>
      </HeroSection>
    </PageWrapper>
  );
};

export default SubmissionsPage;
